import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Navigate, Outlet, useMatch, useNavigate } from 'react-router-dom';
import { Theme } from '@app/theme';
import {
  ProfileFieldCategory,
  useCurrentParticipant,
  useLogout,
  useMe,
} from '@app/api';
import {
  Button,
  CenteredLoader,
  useProfileCompletion,
  WithRightClosablePane,
} from '@app/core';
import { getLocale } from '@app/i18n';

import { RecapCard } from './components/RecapCard';
import { useTranslation } from 'react-i18next';
import { VerifiedProfileIconsCard } from '../Home/components/VerifiedProfileIconsCard';
import { ProfileHeader } from './ProfileHeader';

const useStyles = createUseStyles((theme: Theme) => ({
  page: {
    backgroundColor: theme.colors.grayBackground,
    minHeight: '100%',
  },
  container: {
    margin: '0 auto',
    width: '100%',
    maxWidth: 456,
    paddingTop: 0,
    [theme.media.largeScreen]: {
      paddingTop: theme.marginBase * 7,
    },
  },
  elementContainer: {
    paddingLeft: theme.marginBase * 2,
    paddingRight: theme.marginBase * 2,
    [theme.media.largeScreen]: {
      padding: 0,
    },
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.grayBorder,
    margin: [theme.marginBase * 3, 0],
  },
  buttonContainer: {
    width: '100%',
  },
  verified: {
    marginBottom: theme.marginBase * 2,
    padding: [theme.marginBase * 2, theme.marginBase * 3],
  },
  version: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 4,
    textAlign: 'center',
  },
}));
export const Profile = () => {
  const classes = useStyles();
  const { data: me, isLoading } = useMe();
  const { data: participant, isLoading: isParticipantLoading } =
    useCurrentParticipant();
  const completion = useProfileCompletion();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const match = useMatch('/profile/:name/*');
  const matchedPage = match?.params.name;
  const isOpen = !!matchedPage;
  const { mutate: logout } = useLogout();

  const [_isOpen, setOpen] = useState(true);
  const close = useCallback(() => setOpen(false), [setOpen]);

  const onHelpClick = useCallback(() => {
    if (matchedPage) {
      navigate('/profile');
    }
    return false;
  }, [matchedPage]);

  if (isLoading || isParticipantLoading) {
    return <CenteredLoader />;
  }
  if (!me || !participant) {
    return <Navigate to={'/login'} />;
  }

  return (
    <WithRightClosablePane
      pane={() => <Outlet />}
      onRequestClose={close}
      isOpen={isOpen}
    >
      <div className={classes.page}>
        <div className={classes.container}>
          <ProfileHeader participant={participant} />
          <div className={classes.elementContainer}>
            <VerifiedProfileIconsCard className={classes.verified} />
            <RecapCard
              icon="ri-smartphone-line"
              title={t('participantSpace.profile.contactDetails')}
              to="contact-details"
              isOpen={matchedPage === 'contact-details'}
              completion={
                completion?.[ProfileFieldCategory.CONTACT_DETAILS]?.percentage
              }
            />
            <RecapCard
              icon="ri-user-3-line"
              title={t('participantSpace.profile.personalProfile')}
              to="personal-profile"
              isOpen={matchedPage === 'personal-profile'}
              completion={
                completion?.[ProfileFieldCategory.PERSONAL]?.percentage
              }
            />
            <RecapCard
              icon="ri-briefcase-3-line"
              title={t('participantSpace.profile.professionalProfile')}
              to="professional-profile"
              isOpen={matchedPage === 'professional-profile'}
              completion={
                completion?.[ProfileFieldCategory.PROFESSIONAL]?.percentage
              }
            />
            <RecapCard
              icon="ri-computer-line"
              title={t('participantSpace.profile.ownedDevices')}
              to="owned-devices"
              isOpen={matchedPage === 'owned-devices'}
              completion={
                completion?.[ProfileFieldCategory.OWNED_DEVICES]?.percentage
              }
            />
            <div className={classes.separator} />
            <RecapCard
              icon="ri-star-line"
              title={t('participantSpace.profile.studyPreferences')}
              to="study-preferences"
              isOpen={matchedPage === 'study-preferences'}
            />
            <RecapCard
              icon="ri-settings-3-line"
              title={t('participantSpace.profile.settings')}
              to="settings"
              isOpen={matchedPage === 'settings'}
            />
            <RecapCard
              icon="ri-question-line"
              title={t('participantSpace.profile.help')}
              onClick={onHelpClick}
              target="_blank"
              rel="noopener noreferrer"
              href={
                getLocale() === 'fr'
                  ? 'https://tandemz.crisp.help/fr/category/aide-pour-les-participants-1h2swz9/'
                  : 'https://tandemz.crisp.help/en/category/participants-helpdesk-12yfevd/'
              }
            />
            <Button
              color="red"
              variant="transparent"
              className={classes.buttonContainer}
              onClick={() => logout()}
            >
              {t('participantSpace.profile.logOut')}
            </Button>

            <div className={classes.version}>v{process.env.NX_APP_VERSION}</div>
          </div>
        </div>
      </div>
    </WithRightClosablePane>
  );
};
